import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Linkedin, ChevronDown, ChevronUp, ArrowRight, GraduationCap } from 'lucide-react';
import { founder, successStories, teamHighlights, companyInfo, experienceSection, COMPANY_LOGO } from '../data/mockData';
import WhatsAppFloatingButton from "../pages/WhatsAppFloatingButton";
import { Oval } from 'react-loader-spinner';
const AboutPage = () => {
  const [isFounderExpanded, setIsFounderExpanded] = useState(false);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(()=>{
    setLoading(true);
    fetch(COMPANY_LOGO)
      .then((res) => res.json())
      .then((data) => {
        setClientLogos(data);
        setLoading(false)
      })
      .catch((err) => {
        console.error(err);
        setLoading(false)
      });
  },[])
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-ice-blue relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80" 
            alt="Team"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-3xl"
          >
            <motion.span variants={itemVariants} className="service-tag bg-sage-light text-sage">
              About Us
            </motion.span>
            <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl font-bold mt-6 mb-8 text-navy">
              Our <span className="text-royal-blue">Story</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-xl text-steel-blue leading-relaxed">
              We are a nimble team of seasoned professionals with complementary expertise across finance, banking, design, storytelling, and production. Guided by the maturity and discipline that come with experience, we harness smart technology to deliver timely, tailored solutions for our clients.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Meet Our Founder - Expandable */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <span className="service-tag bg-royal-blue text-white">Leadership</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 text-navy">Meet Our Founder</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="max-w-4xl mx-auto">
              <div className="bg-snow rounded-lg p-8 md:p-12 border border-gray-100">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  <div className="w-40 h-40 rounded-lg bg-light-blue overflow-hidden flex-shrink-0">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-navy mb-1">{founder.name}</h3>
                    <p className="text-steel-blue mb-4">{founder.title}</p>
                    <a
                      href={founder.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-royal-blue hover:underline mb-6"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span>Connect on LinkedIn</span>
                    </a>
                    
                    <div className="prose prose-slate max-w-none">
                      <p className="text-steel-blue leading-relaxed">
                        {founder.shortBio}
                      </p>
                      
                      <AnimatePresence>
                        {isFounderExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 space-y-4 overflow-hidden"
                          >
                            {founder.fullBio.split('\n\n').slice(1).map((paragraph, index) => (
                              <p key={index} className="text-steel-blue leading-relaxed">
                                {paragraph}
                              </p>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    
                    <button
                      onClick={() => setIsFounderExpanded(!isFounderExpanded)}
                      className="mt-6 inline-flex items-center text-royal-blue font-semibold hover:underline"
                    >
                      {isFounderExpanded ? (
                        <>Read Less <ChevronUp className="w-4 h-4 ml-1" /></>
                      ) : (
                        <>Read More <ChevronDown className="w-4 h-4 ml-1" /></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Experience */}
      {/* <section className="py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.div variants={itemVariants}>
              <span className="service-tag bg-light-blue text-royal-blue">{experienceSection.title}</span>
              <p className="text-steel-blue mt-4 max-w-2xl mx-auto">{experienceSection.description}</p>
            </motion.div>
            
            <motion.div variants={itemVariants} className="flex flex-wrap justify-center items-center gap-8 lg:gap-16 mt-12">
              {clientLogos.map((client, index) => (
                <img 
                  key={index}
                  src={client.logo} 
                  alt={client.name} 
                  className="h-12 lg:h-14 object-contain client-logo" 
                />
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section> */}
      <section className="py-20 bg-white border-y border-gray-100">
  <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once:true, margin: "-100px" }}
      variants={containerVariants}
      className="text-center"
    >
      <motion.div variants={itemVariants}>
        <span className="service-tag bg-light-blue text-royal-blue">
          {experienceSection.title}
        </span>
        <p className="text-steel-blue mt-4 max-w-2xl mx-auto">
          {experienceSection.description}
        </p>
      </motion.div>

      {/* AUTO SCROLL LOGO WRAPPER */}
        <motion.div variants={itemVariants} className="relative overflow-hidden mt-12">
       <div className="flex items-center gap-20 animate-scroll">
       {clientLogos.concat(clientLogos).map((client, index) => (
       <div
       key={index}
       className="w-56 h-32 flex items-center justify-center"
       >
       <img
       src={`${client.image_url}`}
       alt={client.title}
       className="max-w-full max-h-full object-contain"
       />
       </div>
       ))}
       </div>
      </motion.div>
    </motion.div>
           <div style={{
      display:"flex",
      justifyContent:"center",
      alignItems:"center",
      marginBottom:"20px"
    }}>
    {loading &&  <Oval
      height={80}
      width={80}
      color="#4fa94d"
      wrapperStyle={{}}
      wrapperClass=""
      visible={true}
      ariaLabel='oval-loading'
      secondaryColor="#4fa94d"
      strokeWidth={2}
      strokeWidthSecondary={2}
    />
}
</div>
  </div>
</section>


      {/* Client Success Stories */}
      {/* <section className="py-24 lg:py-32 bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <span className="service-tag bg-light-blue text-royal-blue">Success Stories</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4">Client Success Stories</h2>
            </motion.div>

            <motion.div variants={itemVariants} className="grid lg:grid-cols-2 gap-16 items-start"> */}
              {/* Founder Image & Info */}
              {/* <div className="bg-white/5 rounded-lg p-8 border border-white/10">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  <div className="w-36 h-36 rounded-lg bg-light-blue overflow-hidden flex-shrink-0">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{founder.name}</h3>
                    <p className="text-gray-400 mb-4">{founder.title}</p>
                    <a
                      href={founder.linkedIn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-soft-blue hover:text-blue-300 transition-colors"
                    >
                      <Linkedin className="w-5 h-5" />
                      <span>Connect on LinkedIn</span>
                    </a>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-gray-300 leading-relaxed">{founder.bio}</p>
                </div> */}

                {/* Experience */}
                {/* <div className="mt-8">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4">Experience</h4>
                  <ul className="space-y-3">
                    {founder.experience.map((exp, index) => (
                      <li key={index} className="text-gray-300 text-sm flex items-start">
                        <span className="w-2 h-2 rounded-full bg-soft-blue mt-2 mr-3 flex-shrink-0" />
                        {exp}
                      </li>
                    ))}
                  </ul>
                </div>
              </div> */}

              {/* Expertise & Education */}
              {/* <div className="space-y-8"> */}
                {/* Expertise */}
                {/* <div className="bg-white/5 rounded-lg p-8 border border-white/10">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-6">Areas of Expertise</h4>
                  <div className="flex flex-wrap gap-3">
                    {founder.expertise.map((skill, index) => (
                      <span
                        key={index}
                        className="service-tag bg-light-blue text-royal-blue"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div> */}

                {/* Education */}
                {/* <div className="bg-white/5 rounded-lg p-8 border border-white/10">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-6">Education</h4>
                  <ul className="space-y-3">
                    {founder.education.map((edu, index) => (
                      <li key={index} className="text-gray-300 flex items-start">
                        <GraduationCap className="w-5 h-5 text-soft-blue mr-3 flex-shrink-0 mt-0.5" />
                        {edu}
                      </li>
                    ))}
                  </ul>
                </div> */}

                {/* Location */}
                {/* <div className="bg-white/5 rounded-lg p-8 border border-white/10">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4">Based In</h4>
                  <p className="text-2xl font-semibold text-sage">{companyInfo.location}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section> */}
<WhatsAppFloatingButton />
      {/* Founder's Publications */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <span className="service-tag bg-light-blue text-royal-blue">Publications</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mt-4 text-navy">Founder's Latest Updates</h2>
            </motion.div>
                     <div class="elfsight-app-0312ced7-b43f-4a0c-838b-35ad89c8073f" data-elfsight-app-lazy></div>

            <motion.div variants={containerVariants} className="grid md:grid-cols-3 gap-6">
              {/* {founderPublications.map((pub) => (
                <motion.a
                  key={pub.id}
                  href={pub.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  variants={itemVariants}
                  className="border border-white/20 p-8 rounded-lg hover:border-white/40 transition-colors bg-white/5"
                >
                  <span className="service-tag bg-light-blue text-royal-blue mb-4">{pub.publication}</span>
                  <h3 className="font-semibold text-lg mb-2 text-navy group-hover:text-royal-blue transition-colors flex items-start">
                    {pub.title}
                    <ArrowUpRight className="w-4 h-4 ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="text-silver text-sm">{pub.date}</p>
                </motion.a>
              ))} */}
     
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-royal-blue">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6">
              Let's Work Together
            </motion.h2>
            <motion.p variants={itemVariants} className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
              Ready to elevate your brand? Let's start a conversation about how we can help.
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link to="/contact" className="cta-button !bg-white !text-royal-blue hover:!bg-gray-100">
                Let's Connect
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
